import murmur3 from "murmurhash-js/murmurhash3_gc";

import type {
  AppControlProps,
  AppControlUnionProps,
} from "../../../controls/AppControlProps";
import type { RibbonControlProps } from "../../Controls/RibbonControlProps";
import type {
  RibbonScalingSafeguardState,
  RibbonScalingState,
  RibbonScalingStateWithCacheKeys,
  ScaledRibbonGroup,
} from "./ribbonBottomBarScalingTypes";

interface LayoutImpactingControlProps {
  controlType: string;
  id: string;
  [props: string]: string | number | object | undefined;
}

/**
 * Gets an object with all properties that impact the layout dimensions of a control.
 * @param controlProps The props for the control under consideration.
 */
export function getLayoutImpactingPropertiesForControl(
  controlProps: AppControlUnionProps
): LayoutImpactingControlProps {
  switch (controlProps.type) {
    case "AppButtonProps":
    case "AppInkButtonProps":
    case "AppToggleButtonProps":
    case "AppFlyoutAnchorProps":
    case "AppInkFlyoutAnchorProps":
    case "AppSplitButtonProps": {
      return {
        controlType: controlProps.type,
        id: controlProps.id,
        label: controlProps.hideLabel ? "" : controlProps.label,
      };
    }
    case "AppCheckboxProps":
    case "AppSpinButtonProps":
    case "AppLabelProps":
    case "AppInputProps":
    case "AppGalleryButtonProps": {
      return {
        controlType: controlProps.type,
        id: controlProps.id,
        label: controlProps.label,
      };
    }
    case "AppFontPickerProps": {
      return {
        controlType: controlProps.type,
        id: controlProps.id,
        width: controlProps.width,
      };
    }
    case "AppComboBoxProps": {
      return {
        controlType: controlProps.type,
        id: controlProps.id,
        width: controlProps.width,
        label: controlProps.showLabel ? controlProps.label : "",
      };
    }
    case "AppIconPreviewGalleryProps":
    case "AppFontStylesPreviewGalleryProps":
    case "RibbonTableStylesPreviewGalleryProps": {
      if (controlProps.renderAsFlyout) {
        return {
          controlType: "AppFlyoutAnchorProps",
          id: controlProps.id,
          label: controlProps.flyoutAnchorProps?.hideLabel
            ? ""
            : controlProps.flyoutAnchorProps?.label,
        };
      }
      return {
        controlType: controlProps.type,
        id: controlProps.id,
        galleryButtons: (
          controlProps.galleryButtons as AppControlUnionProps[]
        ).map((props) => getLayoutImpactingPropertiesForControl(props)),
      };
    }
    case "AppSliderProps": {
      return {
        controlType: controlProps.type,
        id: controlProps.id,
        text: controlProps.text,
      };
    }
    case "AppPromptInputProps":
    case "AppSuggestionProps":
    case "AppSuggestionListProps": {
      return {
        controlType: controlProps.type,
        id: controlProps.id,
        label: controlProps.label,
      };
    }
    default: {
      throw new Error(
        "The following AppControlProps is not valid as a top level ribbon control" +
          controlProps.type
      );
    }
  }
}

/**
 * Gets an array of all the properties that impact the dimensions of the control group.
 * @param ribbonControlGroupProps The control group under consideration.
 */
function getLayoutImpactingPropertiesForControlGroup(
  ribbonControlGroupProps: ScaledRibbonGroup
): LayoutImpactingControlProps[] {
  const layoutImpactingProps: LayoutImpactingControlProps[] = [];
  let layoutImpactingControls: RibbonControlProps[];
  switch (ribbonControlGroupProps.type) {
    case "Multiline":
      layoutImpactingControls = ribbonControlGroupProps.controls;
      layoutImpactingProps.push({
        controlType: "layout",
        id: "layout",
        layout:
          ribbonControlGroupProps.layout &&
          JSON.stringify(ribbonControlGroupProps.layout),
      });
      break;
    case "SingleLine":
      layoutImpactingControls = ribbonControlGroupProps.topLevelControls;
      if (
        ribbonControlGroupProps.overflowControls &&
        ribbonControlGroupProps.overflowControls.length > 0
      ) {
        layoutImpactingProps.push({
          controlType: "button",
          id: "overflow",
          hasOwnOverflow: ribbonControlGroupProps.hasOwnOverflow?.toString(),
          shouldAllowAllControlsInOverflow:
            ribbonControlGroupProps.shouldAllowAllControlsInOverflow?.toString(),
        });
      }
      break;
  }

  layoutImpactingProps.push(
    ...layoutImpactingControls!.map((control) =>
      getLayoutImpactingPropertiesForControl(control)
    )
  );
  return layoutImpactingProps;
}

export function getLayoutKeyForControl(control: AppControlProps): string {
  return murmur3(
    JSON.stringify(getLayoutImpactingPropertiesForControl(control))
  ).toString(36);
}

/**
 * Gets a string that can be used as a key for caching the width of a set of control groups. If two control groups have
 * the same cache key, it can be assumed that their layout dimensions are identical.
 * @param controlGroups The control groups for which the layout key should be computed.
 */
function getLayoutKeyForControlGroups(
  controlGroups: ScaledRibbonGroup[],
  scalingFallbackState?: RibbonScalingSafeguardState
): string {
  // Use murmur3 to hash the cache key to reduce the bytes needed to be stored in memory.
  return murmur3(
    JSON.stringify(
      controlGroups.map((group) =>
        getLayoutImpactingPropertiesForControlGroup(group)
      )
    ) + (scalingFallbackState ?? "")
  ).toString(36);
}

/**
 * Gets a string that can be used as a key for caching the width of a control groups. If two control groups have
 * the same cache key, it can be assumed that their layout dimensions are identical.
 * @param controlGroups The control groups for which the layout key should be computed.
 */
export function getLayoutKeyForControlGroup(
  controlGroup: ScaledRibbonGroup
): string {
  // Use murmur3 to hash the cache key to reduce the bytes needed to be stored in memory.
  return murmur3(
    JSON.stringify(getLayoutImpactingPropertiesForControlGroup(controlGroup))
  ).toString(36);
}

export function addLayoutCacheKeyToRibbonState(
  scalingState: RibbonScalingState,
  scalingFallbackState?: RibbonScalingSafeguardState
): RibbonScalingStateWithCacheKeys {
  return {
    ...scalingState,
    cacheKey: getLayoutKeyForControlGroups(
      scalingState.scaledRibbonGroups,
      scalingFallbackState
    ),
  };
}
